import { Component, Input } from '@angular/core';
import { getDownloadURL, ref, Storage, uploadBytesResumable } from '@angular/fire/storage';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppFeaturesShipmentOrSearchForm } from '../../../pages/form-app/models/app-form';

@Component({
    selector: 'app-background-image-upload',
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './background-image-upload.component.html',
    styleUrl: './background-image-upload.component.scss',
})
export class BackgroundImageUploadComponent {
    @Input({ required: true }) public formGroup!: FormGroup<AppFeaturesShipmentOrSearchForm>;
    @Input() public removeShadowControl?: FormControl<boolean | null>;
    private fallbackRemoveShadow = new FormControl<boolean | null>(false);

    public get effectiveRemoveShadowControl(): FormControl<boolean | null> {
        return this.removeShadowControl ?? this.fallbackRemoveShadow;
    }
    @Input()
    set images(value: { name?: string | null }[]) {
        this.patchBackgroundImages(value);
    }

    public isUploadingBackgroundImg = false;
    public uploadingIndex: number | null = null;

    constructor(
        private storage: Storage,
        private snackBar: MatSnackBar
    ) {}

    get backgroundImages():
        | FormArray<
              FormGroup<{
                  name: FormControl<string | null>;
              }>
          >
        | undefined {
        return this.formGroup?.controls['backgroundImages'];
    }

    get backgroundType(): FormControl<string | null> | undefined {
        return this.formGroup?.controls['backgroundType'];
    }

    delete(index: number): void {
        this.backgroundImages?.removeAt(index);
    }

    addBackgroundImageField(): void {
        this.backgroundImages?.push(
            new FormGroup<{ name: FormControl<string | null> }>({
                name: new FormControl<string>(''),
            })
        );
    }

    onFileSelected(event: Event, type: 'backgroundImg', index: number): void {
        this.uploadingIndex = index;
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;

        let storagePath = `logos/${file.name}`;

        if (type === 'backgroundImg') {
            this.isUploadingBackgroundImg = true;
            storagePath = `backgroundImg/${file.name}`;
        }

        const fileRef = ref(this.storage, storagePath);
        const uploadTask = uploadBytesResumable(fileRef, file);

        uploadTask
            .then(() =>
                getDownloadURL(fileRef).then((url) => {
                    const backgroundImagesArray = this.backgroundImages;
                    if (backgroundImagesArray && backgroundImagesArray.length > 0) {
                        backgroundImagesArray.at(index).get('name')?.setValue(url);
                    }
                    this.isUploadingBackgroundImg = false;
                })
            )
            .catch(() => {
                this.snackBar.open('File upload failed', 'Close', { duration: 3000 });
                this.isUploadingBackgroundImg = false;
            });
    }

    getUniqueId(suffix: string, id: number): string {
        const base = id ?? Date.now();
        return `${suffix}-${base}`;
    }

    patchBackgroundImages(backgroundImages?: { name?: string | null }[]): void {
        if (!this.formGroup || !backgroundImages) return;

        const backgroundImagesCtrl = this.backgroundImages;
        if (!backgroundImagesCtrl) return;

        backgroundImagesCtrl.clear();
        (backgroundImages ?? []).forEach((img) => {
            backgroundImagesCtrl.push(
                new FormGroup<{ name: FormControl<string | null> }>({
                    name: new FormControl<string | null>(img?.name ?? ''),
                })
            );
        });
    }
}
